import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { injectPropsToChildren } from 'utils/injectPropsToChildren';

export interface GuardedComponentProps extends React.PropsWithChildren<{}> {
  allowed: boolean;
  message: string;
  fallback?: React.ReactElement;
  hide?: boolean;
  mode?: 'tooltip' | 'overlay';
  overlayContent?: React.ReactNode;
  endAdornment?: React.ReactNode;
}

export function GuardedComponentOverlayContent({ message }: { message: string }) {
  return (
    <Typography
      variant="h3"
      sx={(theme) => ({
        color: theme.palette.mode === 'dark' ? theme.palette.grey[100] : theme.palette.text.primary,
        textAlign: 'center',
        px: 2,
        fontWeight: 500,
      })}
    >
      {message}
    </Typography>
  );
}

export function GuardedComponent({
  allowed,
  message,
  fallback,
  hide,
  mode = 'tooltip',
  overlayContent,
  endAdornment,
  children,
}: GuardedComponentProps) {
  if (allowed) {
    return children as JSX.Element;
  }

  if (hide) {
    return <></>;
  }

  const injectedChildren = injectPropsToChildren<{ disabled?: boolean }>(children, {
    disabled: true,
  });

  const content = (
    <Box
      sx={{
        position: 'relative',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      {fallback ?? injectedChildren}
    </Box>
  );

  if (mode === 'tooltip') {
    return (
      <Tooltip title={message}>
        <span style={{ position: 'relative' }}>
          {fallback ?? injectedChildren}
          {endAdornment}
        </span>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        height: '100%',
        padding: 2,
      }}
    >
      {content}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          backgroundColor: 'inherit',
          display: 'flex',
          flexDirection: 'column',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          borderRadius: 'inherit',
        }}
      >
        {overlayContent ?? <GuardedComponentOverlayContent message={message} />}
      </Box>

      {endAdornment}
    </Box>
  );
}
